import { appApi } from "@/api/modules/app";

export interface TencentReverseGeocodeResult {
  address: string;
  province?: string;
  city?: string;
  district?: string;
  latitude: number;
  longitude: number;
}

export interface TencentNearbyPlace {
  id: string;
  title: string;
  address: string;
  latitude: number;
  longitude: number;
  distance?: number;
  distanceText?: string;
  isCurrent?: boolean;
}

interface TencentMapGeocodePayload {
  address?: string;
  formatted_addresses?: {
    recommend?: string;
  };
  address_component?: {
    province?: string;
    city?: string;
    district?: string;
  };
}

interface TencentMapPlacePayload {
  id?: string | number;
  title?: string;
  address?: string;
  _distance?: number;
  location?: {
    lat?: number | string;
    lng?: number | string;
  };
}

interface TencentMapResponse<T> {
  status?: number;
  message?: string;
  result?: T;
  data?: TencentMapPlacePayload[];
}

const TENCENT_MAP_GEOCODER_URL = "https://apis.map.qq.com/ws/geocoder/v1/";
const TENCENT_MAP_EXPLORE_URL = "https://apis.map.qq.com/ws/place/v1/explore";
const TENCENT_MAP_SEARCH_URL = "https://apis.map.qq.com/ws/place/v1/search";
const TENCENT_MAP_NATIONWIDE_REGION = "全国";

let tencentMapKeyPromise: Promise<string> | null = null;
let tencentMapSdkPromise: Promise<unknown> | null = null;
let jsonpRequestId = 0;
type WindowWithDynamicCallbacks = Window & Record<string, unknown>;

function formatDistance(distance?: number) {
  if (!distance || distance <= 0) {
    return "";
  }
  if (distance < 1000) {
    return `${Math.round(distance)}m`;
  }
  return `${(distance / 1000).toFixed(1)}km`;
}

function resolveTencentMapError(message: string | undefined, fallback: string) {
  const normalizedMessage = String(message ?? "").trim();
  if (!normalizedMessage) {
    return fallback;
  }
  if (normalizedMessage.includes("来源域名未被授权") || normalizedMessage.includes("无来源信息")) {
    return "腾讯地图地址服务暂未完成授权，请联系管理员补充 WebService Key 或检查域名白名单";
  }
  return normalizedMessage;
}

export async function getTencentMapKey() {
  if (!tencentMapKeyPromise) {
    tencentMapKeyPromise = appApi.getOpenConfig().then((config) => {
      const key = String(config.tencent_map_key ?? "").trim();
      if (!key) {
        throw new Error("腾讯地图 Key 未配置，请先在后台地图设置中补齐");
      }
      return key;
    });
  }
  return tencentMapKeyPromise;
}

async function requestTencentMapJsonp<T>(
  url: string,
  params: Record<string, string | undefined>,
): Promise<TencentMapResponse<T>> {
  const key = await getTencentMapKey();

  return new Promise((resolve, reject) => {
    const callbackName = `__athomeTencentMapCallback${Date.now()}_${jsonpRequestId++}`;
    const scriptId = `athome-tencent-map-jsonp-${callbackName}`;
    const callbackWindow = window as unknown as WindowWithDynamicCallbacks;

    const cleanup = () => {
      delete callbackWindow[callbackName];
      document.getElementById(scriptId)?.remove();
    };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("腾讯地图服务请求超时"));
    }, 8000);

    callbackWindow[callbackName] = (payload: TencentMapResponse<T>) => {
      window.clearTimeout(timeoutId);
      cleanup();
      resolve(payload);
    };

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      const normalizedValue = String(paramValue ?? "").trim();
      if (normalizedValue) {
        searchParams.set(paramKey, normalizedValue);
      }
    });
    searchParams.set("key", key);
    searchParams.set("output", "jsonp");
    searchParams.set("callback", callbackName);

    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      cleanup();
      reject(new Error("腾讯地图服务请求失败"));
    };
    script.src = `${url}?${searchParams.toString()}`;
    document.body.appendChild(script);
  });
}

export async function loadTencentMapSdk() {
  if (typeof window === "undefined") {
    throw new Error("当前环境不支持地图加载");
  }

  if ((window as Window & { TMap?: unknown }).TMap) {
    return (window as Window & { TMap?: unknown }).TMap;
  }

  if (!tencentMapSdkPromise) {
    tencentMapSdkPromise = getTencentMapKey().then(
      (key) =>
        new Promise<unknown>((resolve, reject) => {
          const existing = document.getElementById("athome-tencent-map-sdk");
          if (existing) {
            existing.addEventListener("load", () => resolve((window as Window & { TMap?: unknown }).TMap), {
              once: true,
            });
            existing.addEventListener("error", () => reject(new Error("腾讯地图脚本加载失败")), {
              once: true,
            });
            return;
          }

          const script = document.createElement("script");
          script.id = "athome-tencent-map-sdk";
          script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${encodeURIComponent(key)}`;
          script.async = true;
          script.onload = () => resolve((window as Window & { TMap?: unknown }).TMap);
          script.onerror = () => reject(new Error("腾讯地图脚本加载失败"));
          document.body.appendChild(script);
        }),
    );
  }

  return tencentMapSdkPromise;
}

export async function reverseGeocode(latitude: number, longitude: number): Promise<TencentReverseGeocodeResult> {
  const location = `${latitude},${longitude}`;
  let response: TencentMapResponse<TencentMapGeocodePayload>;

  try {
    response = await requestTencentMapJsonp<TencentMapGeocodePayload>(TENCENT_MAP_GEOCODER_URL, {
      location,
    });
  } catch {
    response = await appApi.getGeocoderCoordinate({ location });
  }

  if (response.status !== 0 || !response.result) {
    throw new Error(resolveTencentMapError(response.message, "地址解析失败，请稍后重试"));
  }

  return {
    address: response.result.formatted_addresses?.recommend || response.result.address || "",
    province: response.result.address_component?.province,
    city: response.result.address_component?.city,
    district: response.result.address_component?.district,
    latitude,
    longitude,
  };
}

export async function searchNearbyPlaces(params: {
  latitude: number;
  longitude: number;
  keyword?: string;
  city?: string;
}) {
  const keyword = params.keyword?.trim() || undefined;
  const city = params.city?.trim() || "";
  const boundary = keyword
    ? `region(${city || TENCENT_MAP_NATIONWIDE_REGION},1)`
    : `nearby(${params.latitude},${params.longitude},1000,1)`;
  let response: TencentMapResponse<never>;

  try {
    response = await requestTencentMapJsonp<never>(
      keyword ? TENCENT_MAP_SEARCH_URL : TENCENT_MAP_EXPLORE_URL,
      {
        orderby: "_distance",
        boundary,
        keyword,
      },
    );
  } catch {
    response = await appApi.getNearbyLocation({
      latitude: params.latitude,
      longitude: params.longitude,
      keyword,
    });
  }

  if (response.status !== undefined && response.status !== 0) {
    throw new Error(resolveTencentMapError(response.message, "附近地点加载失败，请稍后重试"));
  }

  return (response.data ?? [])
    .filter((item) => item.location?.lat !== undefined && item.location?.lng !== undefined)
    .map<TencentNearbyPlace>((item, index) => ({
      id: String(item.id ?? `${item.title || "place"}-${index}`),
      title: item.title || "附近地点",
      address: item.address || "",
      latitude: Number(item.location?.lat),
      longitude: Number(item.location?.lng),
      distance: item._distance,
      distanceText: formatDistance(item._distance),
    }))
    .filter((item) => Number.isFinite(item.latitude) && Number.isFinite(item.longitude));
}
